import { Mail, Phone, MapPin, Linkedin, Globe, Github, Calendar, Award } from 'lucide-react'
import { getColorClasses } from '@/lib/templateColors'

interface ResumeTemplateProps {
  data: any
  language?: string
  labels: any
  color?: string
}


export default function CrimsonRedTemplate({ data, labels, color = 'blue' }: ResumeTemplateProps) {
  const colors = getColorClasses(color)
  const { personalInfo, summary, experience, education, skills, projects, certifications } = data

  return (

      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        <div id="resume-preview" className="bg-white" style={{ width: '8.5in', minHeight: '11in' }}>
          {/* Crimson Header */}
          <div className={`bg-gradient-to-br ${colors.headerBg} ${colors.headerText} p-10`}>
            <h1 className="text-6xl font-black mb-4 tracking-wide">{personalInfo.fullName || 'YOUR NAME'}</h1>
            <div className={`h-1 w-32 ${colors.accentBorder} mb-4`}></div>
            <div className="grid grid-cols-2 gap-4 text-sm font-medium">
              {personalInfo.email && <div className="flex items-center space-x-2"><Mail className="w-4 h-4" /><span>{personalInfo.email}</span></div>}
              {personalInfo.phone && <div className="flex items-center space-x-2"><Phone className="w-4 h-4" /><span>{personalInfo.phone}</span></div>}
              {personalInfo.linkedin && <div className="flex items-center space-x-2"><Linkedin className="w-4 h-4" /><span>{personalInfo.linkedin}</span></div>}
              {personalInfo.website && <div className="flex items-center space-x-2"><Globe className="w-4 h-4" /><span>{personalInfo.website}</span></div>}
            </div>
          </div>

          <div className="p-8">
            {summary && (
              <div className="mb-6">
                <h2 className={`text-2xl font-black ${colors.sectionTitle} mb-3 uppercase tracking-wide border-b-4 ${colors.accentBorder} pb-2`}>{labels.professionalSummary}</h2>
                <p className="text-gray-700 leading-relaxed text-justify">{summary}</p>
              </div>
            )}

            {experience && experience.length > 0 && (
              <div className="mb-6">
                <h2 className={`text-2xl font-black ${colors.sectionTitle} mb-5 uppercase tracking-wide border-b-4 ${colors.accentBorder} pb-2`}>{labels.workExperience}</h2>
                <div className="space-y-6">
                  {experience.map((exp: any) => (
                    <div key={exp.id} className="relative pl-8">
                      <div className={`absolute left-0 top-0 bottom-0 w-1 ${colors.timelineBorder}`}></div>
                      <div className={`absolute left-0 top-2 w-4 h-4 ${colors.timelineDot} rounded-full -ml-1.5`}></div>
                      <h3 className="text-xl font-bold text-gray-900">{exp.jobTitle}</h3>
                      <div className={`${colors.accentText} font-bold text-lg`}>{exp.company}</div>
                      <div className="text-sm text-gray-600 font-semibold mb-3">{exp.startDate} - {exp.endDate || labels.present} | {exp.location}</div>
                      <div className="text-gray-700 whitespace-pre-line">{exp.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {skills && skills.length > 0 && (
              <div className="mb-6">
                <h2 className={`text-2xl font-black ${colors.sectionTitle} mb-5 uppercase tracking-wide border-b-4 ${colors.accentBorder} pb-2`}>{labels.skills}</h2>
                <div className="grid grid-cols-3 gap-4">
                  {skills.map((skillGroup: any, idx: number) => (
                    <div key={idx} className={`${colors.badgeBg} p-4 border-t-4 ${colors.accentBorder}`}>
                      <div className={`font-black ${colors.sectionTitle} mb-3 text-center`}>{skillGroup.category}</div>
                      <div className="space-y-2">
                        {skillGroup.skills?.map((skill: string, skillIdx: number) => (
                          <div key={skillIdx} className="text-sm text-gray-700 text-center font-medium">
                            {skill}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {education && education.length > 0 && (
              <div>
                <h2 className={`text-2xl font-black ${colors.sectionTitle} mb-5 uppercase tracking-wide border-b-4 ${colors.accentBorder} pb-2`}>{labels.education}</h2>
                <div className="space-y-4">
                  {education.map((edu: any) => (
                    <div key={edu.id} className="flex items-start space-x-4">
                      <Award className={`w-8 h-8 ${colors.accentText} mt-1 flex-shrink-0`} />
                      <div>
                        <div className="font-bold text-gray-900 text-lg">{edu.degree}</div>
                        <div className={`${colors.accentText} font-semibold`}>{edu.institution}</div>
                        <div className="text-sm text-gray-600">{edu.graduationDate}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  )
}
